import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useLayoutEffect,
} from 'react';
import axios from '../lib/axios';

interface ThemeContextType {
  theme: 'light' | 'dark';
  toggleTheme: () => void;
  setTheme: (theme: 'light' | 'dark') => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Global key that persists regardless of shop - survives refresh even before auth loads
const THEME_KEY = 'netsight_theme';

const getSavedTheme = (): 'light' | 'dark' => {
  try {
    const saved = localStorage.getItem(THEME_KEY);
    if (saved === 'light' || saved === 'dark') return saved;
  } catch { /* ignore */ }
  return 'light';
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
  shop: string;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  shop,
}) => {
  const [theme, setThemeState] = useState<'light' | 'dark'>(getSavedTheme);

  // Apply saved theme before first paint - uses global key, no dependency on shop
  useLayoutEffect(() => {
    const saved = getSavedTheme();
    const root = document.documentElement;
    root.classList.toggle('dark', saved === 'dark');
    setThemeState(saved);
  }, []);

  // When shop becomes available, check if billing page needs forced dark
  useEffect(() => {
    if (window.location.pathname === '/billing/choose-plan') {
      document.documentElement.classList.add('dark');
      setThemeState('dark');
    }
  }, [shop]);

  // SET THEME (toggle + manual)
  const setTheme = async (newTheme: 'light' | 'dark') => {
    try {
      setThemeState(newTheme);
      document.documentElement.classList.toggle('dark', newTheme === 'dark');

      // Save to global key (persists across refresh)
      localStorage.setItem(THEME_KEY, newTheme);

      // Also save to shop-specific key for backward compat
      if (shop && shop !== 'undefined') {
        localStorage.setItem(`theme-${shop}`, newTheme);
      }

      // API save (fire and forget)
      if (shop && shop !== 'undefined') {
        const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '';
        const apiUrl = apiBaseUrl ? `${apiBaseUrl}/theme` : '/api/theme';
        await axios.post(apiUrl, { shop, theme: newTheme });
      }
    } catch (error) {
      console.error('Error saving theme:', error);
    }
  };

  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
